import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LocalStorageService } from '@/core/services/utils/local-storage.service';
import { NewsRepository } from '@/core/repositories/news-repository';
import { TranslateService } from '@ngx-translate/core';

export interface News {
    id: string;
    bannerText: string;
    dialogTitle?: string;
    dialogContent?: string;
    actionText?: string;
    customAction?: () => void; // if set, this will be used instead of opening the dialog
}

/**
 * @class NewsService
 * This service manages the news to be displayed in the top bar.
 *
 * @see See TopBarNewsComponent
 */
@Injectable({ providedIn: 'root' })
export class NewsService {
    /**
     * News to be displayed at the top of the page. Hidden news items are filtered out.
     */
    private news = new BehaviorSubject<News[]>([]);
    /**
     * News to be displayed at the top of the page. Hidden news items are filtered out.
     * @returns Observable<News[]> - The list of news to be displayed.
     */
    public news$ = this.news.asObservable();
    /**
     * All news messages, including hidden ones.
     */
    private allNews: News[] = [];

    constructor(
        private readonly newsRepository: NewsRepository,
        private readonly translate: TranslateService,
    ) {
        this.translate.onLangChange.subscribe(() => this.reloadNews());
    }

    public refreshNews(): void {
        const hiddenIds = this.getHiddenNewsIds();
        const newsToDisplay = this.allNews.filter((n) => !hiddenIds.includes(n.id));
        this.news.next(newsToDisplay);
    }

    public loadNews(): void {
        this.newsRepository.getNews().subscribe((news: News[]) => {
            this.allNews = news;
            this.refreshNews();
        });
    }

    public reloadNews(): void {
        this.loadNews();
    }

    /**
     * Hides a news message permanently by storing the ID in local storage.
     * @param newsId The id of the message to hide.
     */
    public hideNewsMessage(newsId: string): void {
        const hiddenIds = this.getHiddenNewsIds();
        hiddenIds.push(newsId);
        LocalStorageService.storeHiddenNewsIds(hiddenIds.join(','));
        this.refreshNews();
    }

    public clearNews(): void {
        this.allNews = [];
        this.refreshNews();
    }

    private getHiddenNewsIds(): string[] {
        const ids = LocalStorageService.getHiddenNewsIds();
        if (!ids) {
            return [];
        }
        return ids.split(',');
    }
}
